import {
    useEffect,
    useState,
    useRef
} from "react";
import {
    useWindowDimensions,
    TouchableOpacity,
    Platform,
    Image,
    Modal,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    MarkdownEditor,
    DateTimePicker,
    PageContainer,
    TextAreaInput,
    TextInput,
    EnUsIcon,
    TRTRIcon,
    Dialog,
    Button,
    Switch,
    Text
} from "ncore-ui-kit";
import {
    useNavigation,
    useRoute
} from "@react-navigation/native";
import type {
    PostPhotoItem
} from "../../../../components/postPhotosManager/type";
import type RootStackParamList from "../../../../navigation/type";
import type {
    PostPhotoType
} from "../../../../services/restAPI/actions/updateAnnouncement/type";
import type {
    RouteProp
} from "@react-navigation/native";
import {
    ArrowLeftIcon,
    PencilIcon,
    TrashIcon,
    ImageIcon,
    PlusIcon,
    SaveIcon,
    XIcon
} from "lucide-react-native";
import {
    type NativeStackNavigationProp
} from "@react-navigation/native-stack";
import AsyncStorage from "@react-native-async-storage/async-storage";
import DriveSelectorModal from "../../../../components/driveSelectorModal";
import PostPhotosManager from "../../../../components/postPhotosManager";
import RESTServiceContext from "../../../../services/restAPI/context";
import SmartCoverImage from "../../../../components/smartCoverImage";

const resolvePhotoUrl = (
    photo?: { path?: string; url?: string; } | string,
    authToken?: string
): string => {
    if(!photo) {
        return "";
    }

    let targetUrl = "";

    if(typeof photo === "string") {
        targetUrl = photo;
    } else if(photo.url && photo.url.startsWith("http")) {
        targetUrl = photo.url;
    } else if(photo.path) {
        targetUrl = photo.path;
    }

    if(!targetUrl) {
        return "";
    }

    if(targetUrl.includes("/uploads/drive/")) {
        targetUrl = targetUrl.replace("/uploads/drive/", "/drive/");
    }

    const isDrive = targetUrl.includes("/drive/");
    if(isDrive) {
        if(!targetUrl.startsWith("http")) {
            const cleanPath = targetUrl.startsWith("/") ? targetUrl : `/${targetUrl}`;
            targetUrl = `${process.env.STORAGE_URL}${cleanPath}`;
        }
        if(authToken && !targetUrl.includes("token=")) {
            const separator = targetUrl.includes("?") ? "&" : "?";
            targetUrl = `${targetUrl}${separator}token=${authToken}`;
        }
        return targetUrl;
    }

    if(targetUrl.startsWith("http") || targetUrl.startsWith("blob:")) {
        return targetUrl;
    }

    const cleanPath = targetUrl.startsWith("/uploads")
        ? targetUrl
        : `/uploads${targetUrl.startsWith("/") ? "" : "/"}${targetUrl}`;

    return `${process.env.STORAGE_URL}${cleanPath}`;
};

type AnnouncementLanguageData = {
    notify?: {
        targetDate: Date | string;
        description?: string;
        title: string;
    }[];
    shortContent?: string;
    language: string;
    content?: string;
    title: string;
};

const AnnouncementEdit = () => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const route = useRoute<RouteProp<RootStackParamList, "AnnouncementEdit">>();
    const announcementID = route.params?.announcementID;

    const [
        languages
    ] = useState<string[]>([
        "tr-TR",
        "en-US"
    ]);

    const [
        currentLang,
        setCurrentLang
    ] = useState<string>("tr-TR");

    const [
        data,
        setData
    ] = useState<AnnouncementLanguageData[]>([
        {
            language: "tr-TR",
            shortContent: "",
            content: "",
            notify: [],
            title: ""
        },
        {
            language: "en-US",
            shortContent: "",
            content: "",
            notify: [],
            title: ""
        }
    ]);

    const [
        isActive,
        setIsActive
    ] = useState<boolean>(true);

    const [
        isFeatured,
        setIsFeatured
    ] = useState<boolean>(false);

    const [
        eventDate,
        setEventDate
    ] = useState<Date | undefined>(undefined);

    const [
        coverPreview,
        setCoverPreview
    ] = useState<string>("");

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(true);

    const [
        isDialogVisible,
        setIsDialogVisible
    ] = useState<boolean>(false);

    const [
        isDeleteDialogOpen,
        setIsDeleteDialogOpen
    ] = useState<boolean>(false);

    const [
        isDeleting,
        setIsDeleting
    ] = useState<boolean>(false);

    const [
        isDriveModalVisible,
        setIsDriveModalVisible
    ] = useState<boolean>(false);

    const [
        isLightboxVisible,
        setIsLightboxVisible
    ] = useState<boolean>(false);

    const [
        editorKey,
        setEditorKey
    ] = useState<number>(0);

    const coverInputRef = useRef<HTMLInputElement | null>(null);
    const [
        coverFile,
        setCoverFile
    ] = useState<File | null>(null);

    const [
        authToken,
        setAuthToken
    ] = useState<string>("");

    const [
        postPhotos,
        setPostPhotos
    ] = useState<PostPhotoItem[]>([]);

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    useEffect(() => {
        AsyncStorage.getItem("authToken").then((token: string | null) => {
            if(token) {
                setAuthToken(token);
            }
        });
    }, []);

    useEffect(() => {
        if(announcementID) {
            fetchAnnouncement();
        } else {
            navigation.goBack();
        }
    }, [announcementID]);

    const fetchAnnouncement = async () => {
        try {
            setIsLoading(true);
            const response = await RESTServiceContext.action("getAnnouncementById", {
                announcementID: announcementID as string
            });

            if (response.payload) {
                setEventDate(response.payload.eventDate ? new Date(response.payload.eventDate as string) : undefined);
                setIsActive(response.payload.isActive ?? true);
                setIsFeatured(response.payload.isFeatured ?? false);

                const currentToken = await AsyncStorage.getItem("authToken") || authToken;
                if(currentToken && !authToken) {
                    setAuthToken(currentToken);
                }

                if (response.payload.coverPhotoPath) {
                    setCoverPreview(resolvePhotoUrl(response.payload.coverPhotoPath, currentToken || undefined));
                }

                if (response.payload.postPhotos && Array.isArray(response.payload.postPhotos) && response.payload.postPhotos.length > 0) {
                    const loadedPhotos: PostPhotoItem[] = response.payload.postPhotos.map((p, idx) => ({
                        previewUrl: resolvePhotoUrl(p.path, currentToken || undefined),
                        source: p.source || "upload",
                        driveFileID: p.driveFileID,
                        order: p.order ?? idx,
                        title: p.title,
                        path: p.path
                    }));
                    setPostPhotos(loadedPhotos);
                } else if (response.payload.coverPhotoPath) {
                    setPostPhotos([
                        {
                            previewUrl: resolvePhotoUrl(response.payload.coverPhotoPath, currentToken || undefined),
                            path: response.payload.coverPhotoPath,
                            source: "upload",
                            order: 0
                        }
                    ]);
                }

                // Merge data
                if (response.payload.data && Array.isArray(response.payload.data)) {
                    setData(prev => {
                        const newData = [...prev];
                        response.payload.data.forEach(item => {
                            const index = newData.findIndex(d => d.language === item.language);
                            if (index !== -1) {
                                newData[index] = {
                                    ...newData[index],
                                    ...item
                                };
                            }
                        });
                        return newData;
                    });
                    setEditorKey(prev => prev + 1);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const maxFileSize = 10 * 1024 * 1024;
            if (file.size > maxFileSize) {
                NCoreUIKitSnackBar.open({
                    title: localize("members-invalid-file-size")
                });
                return;
            }
            const preview = URL.createObjectURL(file);
            setCoverFile(file);
            setCoverPreview(preview);
            setPostPhotos(prev => {
                const coverItem: PostPhotoItem = {
                    previewUrl: preview,
                    source: "upload",
                    title: file.name,
                    order: 0,
                    file
                };
                if(prev.length === 0) {
                    return [coverItem];
                }
                const remaining = prev.filter((_, idx) => idx !== 0);
                return [
                    coverItem,
                    ...remaining
                ].map((p, idx) => ({
                    ...p,
                    order: idx
                }));
            });
        }
    };

    const handlePostPhotosChange = (updated: PostPhotoItem[]) => {
        setPostPhotos(updated);
        if(updated.length > 0) {
            const first = updated[0];
            if(first) {
                setCoverPreview(first.previewUrl);
                if(first.file) {
                    setCoverFile(first.file);
                } else {
                    setCoverFile(null);
                }
            }
        } else {
            setCoverPreview("");
            setCoverFile(null);
        }
    };

    const uploadPhoto = async (
        targetID: string,
        file: File
    ) => {
        try {
            const validTypes = [
                "image/jpeg",
                "image/webp",
                "image/png",
                "image/jpg"
            ];
            const fileType = validTypes.includes(file.type) ? file.type : "image/jpeg";
            const uploadFile = file.type === fileType ? file : new File([file], file.name, {
                type: fileType
            });
            const result = await RESTServiceContext.action("getUploadTokenForAnnouncementCover", {
                fileName: uploadFile.name,
                fileSize: uploadFile.size,
                fileType: uploadFile.type,
                announcementID: targetID
            });
            const uploadResult = result as unknown as { payload?: { token?: string; uploadToken?: string } };
            const token = uploadResult.payload?.token || uploadResult.payload?.uploadToken;
            if (token) {
                const formData = new FormData();
                formData.append("file", uploadFile);
                await fetch(`${process.env.STORAGE_URL}/upload`, {
                    headers: {
                        Authorization: token
                    },
                    body: formData,
                    method: "POST"
                });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const uploadPostPhoto = async (
        targetID: string,
        file: File
    ): Promise<string | undefined> => {
        try {
            const validTypes = [
                "image/jpeg",
                "image/webp",
                "image/png",
                "image/jpg"
            ];
            const fileType = validTypes.includes(file.type) ? file.type : "image/jpeg";
            const uploadFile = file.type === fileType ? file : new File([file], file.name, {
                type: fileType
            });
            const result = await RESTServiceContext.action("getUploadTokenForAnnouncementPhoto", {
                fileName: uploadFile.name,
                fileSize: uploadFile.size,
                fileType: uploadFile.type,
                announcementID: targetID
            });
            const uploadResult = result as unknown as {
                payload?: {
                    uploadToken?: string;
                    token?: string;
                    path?: string;
                };
            };
            const token = uploadResult.payload?.token || uploadResult.payload?.uploadToken;
            const path = uploadResult.payload?.path;
            if (token) {
                const formData = new FormData();
                formData.append("file", uploadFile);
                const response = await fetch(`${process.env.STORAGE_URL}/upload`, {
                    headers: {
                        Authorization: token
                    },
                    body: formData,
                    method: "POST"
                });
                if(response.ok) {
                    const uploadData = await response.json();
                    if(uploadData?.payload?.key) {
                        const key = uploadData.payload.key as string;
                        return key.startsWith("/") ? key.slice(1) : key;
                    }
                    return path;
                }
            }
        } catch (err) {
            console.error(err);
        }
        return undefined;
    };

    const handleDelete = async () => {
        setIsDeleteDialogOpen(false);
        try {
            setIsDeleting(true);
            const result = await RESTServiceContext.action("deleteAnnouncement", {
                announcementID: announcementID as string
            });
            if((result as { code: number; message: string; }).code === 200 || (result as { code: number; message: string; }).code === 201) {
                navigation.goBack();
            } else {
                console.error((result as { code: number; message: string; }).message);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsDeleting(false);
        }
    };

    const cleanDrivePath = (rawPath: string): string => {
        let clean = rawPath;
        try {
            if(clean.startsWith("http")) {
                const parsedUrl = new URL(clean);
                clean = parsedUrl.pathname;
            }
        } catch {
            // ignore
        }
        const queryIndex = clean.indexOf("?");
        if(queryIndex !== -1) {
            clean = clean.substring(0, queryIndex);
        }
        if(!clean.startsWith("/")) {
            clean = `/${clean}`;
        }
        return clean;
    };

    const handleConfirmSave = async () => {
        try {
            setIsSubmitting(true);

            const validData = data.filter(d => d.title.trim() !== "");

            if(validData.length === 0) {
                NCoreUIKitSnackBar.open({
                    title: localize("announcement-title-required")
                });
                return;
            }

            const uploadPromises = postPhotos.map(async (item, i): Promise<PostPhotoType | undefined> => {
                if(!item) {
                    return undefined;
                }
                if (item.source === "upload" && item.file) {
                    const uploadedPath = await uploadPostPhoto(announcementID as string, item.file);
                    if (uploadedPath) {
                        return {
                            path: uploadedPath,
                            title: item.title,
                            source: "upload",
                            order: i
                        };
                    }
                    return undefined;
                }
                if (item.path) {
                    let itemPath = item.path;
                    const isDrive = (item.source === "drive" && (itemPath.includes("drive") || Boolean(item.driveFileID))) || itemPath.includes("/drive/");
                    if(isDrive) {
                        itemPath = cleanDrivePath(itemPath);
                    }
                    return {
                        source: isDrive ? "drive" : "upload",
                        driveFileID: item.driveFileID,
                        title: item.title,
                        path: itemPath,
                        order: i
                    };
                }
                return undefined;
            });
            const results = await Promise.all(uploadPromises);
            const finalPostPhotos: PostPhotoType[] = results.filter((p): p is PostPhotoType => Boolean(p));

            let initialCoverPath = finalPostPhotos.length > 0
                ? finalPostPhotos[0]?.path
                : (coverPreview && !coverFile ? coverPreview : undefined);
            if(initialCoverPath && (initialCoverPath.includes("/drive/") || initialCoverPath.includes("drive/"))) {
                initialCoverPath = cleanDrivePath(initialCoverPath);
            }

            const result = await RESTServiceContext.action("updateAnnouncement", {
                ...(eventDate ? {
                    eventDate: eventDate.toISOString()
                } : {}),
                announcementID: announcementID as string,
                coverPhotoPath: initialCoverPath,
                postPhotos: finalPostPhotos,
                data: validData,
                isFeatured,
                isActive
            });

            const updateResult = result as unknown as { code: number };
            if (updateResult.code === 200) {
                if (coverFile && finalPostPhotos.length === 0) {
                    await uploadPhoto(announcementID as string, coverFile);
                }
                NCoreUIKitSnackBar.open({
                    title: localize("announcement-success-update")
                });
                navigation.navigate("AnnouncementsList");
            } else {
                NCoreUIKitSnackBar.open({
                    title: localize("announcement-error-save")
                });
            }
        } catch (error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("announcement-error-save")
            });
        } finally {
            setIsSubmitting(false);
            setIsDialogVisible(false);
        }
    };

    const handleSubmit = async () => {
        const validData = data.filter(d => d.title.trim() !== "");

        if(validData.length === 0) {
            NCoreUIKitSnackBar.open({
                title: localize("announcement-title-required")
            });
            return;
        }

        const tooLongItem = validData.find(d => d.title.trim().length > 200);
        if(tooLongItem) {
            NCoreUIKitSnackBar.open({
                title: localize("news-title-too-long") || "Başlık en fazla 200 karakter olabilir."
            });
            return;
        }

        setIsDialogVisible(true);
    };

    const updateCurrentLangData = <K extends keyof AnnouncementLanguageData>(key: K, value: AnnouncementLanguageData[K]) => {
        setData(prev => {
            const newData = [...prev];
            const index = newData.findIndex(d => d.language === currentLang);
            if(index !== -1) {
                newData[index] = {
                    ...newData[index],
                    [key]: value
                } as AnnouncementLanguageData;
            }
            return newData;
        });
    };

    const currentLangData = data.find(d => d.language === currentLang);

    const handleDriveFileSelect = (url: string) => {
        setIsDriveModalVisible(false);
        const currentContent = currentLangData?.content || "";
        const imageMarkdown = `\n[image](${url})<100%x300>--contain--\n`;
        updateCurrentLangData("content", currentContent + imageMarkdown);
        setEditorKey(prev => prev + 1);
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    justifyContent: "flex-start",
                    alignItems: "center",
                    flexDirection: "row",
                    gap: spaces.spacingMd
                }
            ]}
        >
            <Button
                icon={() => <ArrowLeftIcon
                    color={colors.project.brandWhite}
                    size={24}
                />}
                customBorderColor="secondaryButton"
                onPress={() => navigation.goBack()}
                customColor="secondaryButton"
                spreadBehaviour="free"
                variant="filled"
            />
            <Text
                variant="titleLargeSize"
            >
                {localize("announcement-edit-title")}
            </Text>
        </View>;
    };

    const renderImagesSection = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.project.brandBorder,
                    borderWidth: borders.seperator,
                    marginTop: spaces.spacingLg,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingLg
                }
            ]}
        >
            <Text
                style={{
                    marginBottom: spaces.spacingMd
                }}
                variant="titleMediumSize"
            >
                {localize("announcement-cover-photo") || "Kapak Fotoğrafı"}
            </Text>
            <View
                style={[
                    stylesheet.imageUploadGrid,
                    {
                        flexDirection: isMobile ? "column" : "row",
                        gap: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={stylesheet.imageUploadCol}
                >
                    {Platform.OS === "web" && (
                        <input
                            onChange={handleCoverSelect}
                            style={{
                                display: "none"
                            }}
                            ref={coverInputRef}
                            accept="image/*"
                            type="file"
                        />)}
                    <View
                        style={[
                            stylesheet.uploadBox,
                            {
                                backgroundColor: colors.project.brandBorder,
                                borderColor: colors.project.brandBorder,
                                borderWidth: borders.seperator,
                                borderRadius: radiuses.md
                            }
                        ]}
                    >
                        {
                            coverPreview ?
                                <TouchableOpacity
                                    onPress={() => setIsLightboxVisible(true)}
                                    style={stylesheet.uploadPreview}
                                    activeOpacity={0.8}
                                >
                                    <TouchableOpacity
                                        onPress={() => coverInputRef.current?.click()}
                                        style={[
                                            stylesheet.editButton,
                                            {
                                                backgroundColor: colors.system.scrim,
                                                padding: spaces.spacingSm,
                                                borderRadius: radiuses.sm,
                                                right: spaces.spacingSm,
                                                top: spaces.spacingSm
                                            }
                                        ]}
                                    >
                                        <PencilIcon
                                            color={colors.project.brandWhite}
                                            size={20}
                                        />
                                    </TouchableOpacity>
                                    <SmartCoverImage
                                        containerStyle={stylesheet.uploadPreview}
                                        url={coverPreview}
                                    />
                                </TouchableOpacity>
                            :
                                <TouchableOpacity
                                    onPress={() => coverInputRef.current?.click()}
                                    style={[
                                        stylesheet.uploadPlaceholder,
                                        {
                                            padding: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    <ImageIcon
                                        color={colors.content.icon.mid}
                                        size={48}
                                    />
                                    <Text
                                        style={[
                                            stylesheet.uploadText,
                                            {
                                                color: colors.content.text.mid,
                                                marginTop: spaces.spacingSm
                                            }
                                        ]}
                                        variant="bodyMediumSize"
                                    >
                                        {localize("announcement-select-cover") || "Kapak fotoğrafı seçmek için tıklayın"}
                                    </Text>
                                    <Text
                                        style={[
                                            stylesheet.uploadLimitText,
                                            {
                                                color: colors.content.text.mid,
                                                marginTop: spaces.spacingXs
                                            }
                                        ]}
                                        variant="bodySmallSize"
                                    >
                                        {localize("events-cover-dimensions")}
                                    </Text>
                                    <Text
                                        style={[
                                            stylesheet.uploadLimitText,
                                            {
                                                color: colors.content.text.mid,
                                                marginTop: spaces.spacingXs
                                            }
                                        ]}
                                        variant="bodySmallSize"
                                    >
                                        {localize("members-max-file-size") || "Maksimum dosya boyutu: 5 MB"}
                                    </Text>
                                </TouchableOpacity>
                        }
                    </View>
                    <View
                        style={{
                            marginTop: spaces.spacingLg
                        }}
                    >
                        <PostPhotosManager
                            onChange={handlePostPhotosChange}
                            photos={postPhotos}
                        />
                    </View>
                </View>
            </View>
        </View>;
    };

    const renderTabs = () => {
        return <View
            style={[
                stylesheet.tabsContainer,
                {
                    marginTop: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <Text
                customColor={colors.content.text.high}
                variant="bodyMediumSize"
            >
                Dil:
            </Text>
            {languages.map(lang => <Button
                customBorderColor={currentLang === lang ? colors.content.border.emphasized : undefined}
                variant={currentLang === lang ? "filled" : "outline"}
                icon={lang.startsWith("tr") ? TRTRIcon : EnUsIcon}
                title={(lang.split("-")[0] || lang).toUpperCase()}
                onPress={() => setCurrentLang(lang)}
                spreadBehaviour="free"
                type="neutral"
                size="small"
                key={lang}
            />)}
        </View>;
    };

    const renderForm = () => {
        return <View
            style={[
                stylesheet.formContainer,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.project.brandBorder,
                    borderWidth: borders.seperator,
                    marginTop: spaces.spacingLg,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingLg,
                    gap: spaces.spacingLg
                }
            ]}
        >
            <View
                style={[
                    stylesheet.switchRow,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Switch
                    title={localize("announcement-is-active") || "Aktif mi?"}
                    onPress={() => setIsActive(!isActive)}
                    customTheme={{
                        themeKey: "events_form" as never
                    }}
                    isActive={isActive}
                    type="neutral"
                />
                <Switch
                    title={localize("announcement-is-featured") || "Öne Çıkan?"}
                    onPress={() => setIsFeatured(!isFeatured)}
                    customTheme={{
                        themeKey: "events_form" as never
                    }}
                    isActive={isFeatured}
                    type="neutral"
                />
            </View>
            <TextInput
                placeholder={localize("announcement-title-placeholder") || "Başlık giriniz"}
                onChangeText={val => updateCurrentLangData("title", val)}
                title={localize("announcement-title-label") || "Başlık"}
                customColor={colors.project.inputBackground}
                key={`title-${currentLang}-${editorKey}`}
                value={currentLangData?.title || ""}
                spreadBehaviour="stretch"
            />
            <TextAreaInput
                placeholder={localize("announcement-short-content-placeholder") || "Kısa içerik giriniz"}
                title={localize("announcement-short-content-label") || "Kısa İçerik"}
                onChangeText={val => updateCurrentLangData("shortContent", val)}
                key={`textarea-${currentLang}-${editorKey}`}
                customColor={colors.project.inputBackground}
                value={currentLangData?.shortContent || ""}
                isUpdateOnRealtime={true}
                spreadBehaviour="stretch"
            />
            <View>
                <Text
                    style={{
                        marginBottom: spaces.spacingSm
                    }}
                    variant="titleSmallSize"
                >
                    {localize("announcement-content-label") || "İçerik (Markdown)"}
                </Text>
                <MarkdownEditor
                    onChangeText={val => updateCurrentLangData("content", val)}
                    initialValue={currentLangData?.content || ""}
                    key={`editor-${currentLang}-${editorKey}`}
                    spreadBehaviour="stretch"
                    defaultMode="live"
                />
                <Button
                    title={localize("announcement-add-image-drive") || "Drive'dan Resim Ekle"}
                    onPress={() => setIsDriveModalVisible(true)}
                    icon={({
                        size
                    }) => <ImageIcon
                        color={colors.project.brandWhite}
                        size={size}
                    />}
                    customBorderColor="secondaryButton"
                    style={{
                        marginTop: spaces.spacingMd
                    }}
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    variant="filled"
                />
                <Text
                    style={{
                        marginBottom: spaces.spacingSm,
                        marginTop: spaces.spacingXl
                    }}
                    variant="titleSmallSize"
                >
                    {localize("announcement-notify-label") || "Bildirim Hedefleri"}
                </Text>
                {
                    currentLangData?.notify?.map((notif, index) => {
                        return <View
                            style={[
                                {
                                    borderColor: colors.project.brandBorder,
                                    borderWidth: borders.seperator,
                                    marginBottom: spaces.spacingMd,
                                    borderRadius: radiuses.md,
                                    padding: spaces.spacingLg
                                }
                            ]}
                            key={`notify-${index}`}
                        >
                            <View
                                style={[
                                    stylesheet.headerContainer,
                                    {
                                        marginBottom: spaces.spacingMd
                                    }
                                ]}
                            >
                                <Text
                                    variant="titleSmallSize"
                                >
                                    {localize("announcement-notify-item") || `Bildirim ${index + 1}`}
                                </Text>
                                <Button
                                    onPress={() => {
                                        const newNotify = [
                                            ...(currentLangData.notify || [])
                                        ];
                                        newNotify.splice(index, 1);
                                        updateCurrentLangData("notify", newNotify);
                                    }}
                                    icon={({
                                        color,
                                        size
                                    }) => <TrashIcon
                                        color={colors.content.icon[color]}
                                        size={size}
                                    />}
                                    spreadBehaviour="free"
                                    type="danger"
                                />
                            </View>
                            <TextInput
                                title={localize("announcement-notify-title") || "Bildirim Başlığı"}
                                onChangeText={val => {
                                    const newNotify = [
                                        ...(currentLangData.notify || [])
                                    ];
                                    newNotify[index]!.title = val;
                                    updateCurrentLangData("notify", newNotify);
                                }}
                                customColor={colors.project.inputBackground}
                                value={notif.title}
                            />
                            <TextAreaInput
                                title={localize("announcement-notify-description") || "Açıklama"}
                                key={`textarea-notify-${currentLang}-${index}-${editorKey}`}
                                onChangeText={val => {
                                    const newNotify = [
                                        ...(currentLangData.notify || [])
                                    ];
                                    newNotify[index]!.description = val;
                                    updateCurrentLangData("notify", newNotify);
                                }}
                                customColor={colors.project.inputBackground}
                                value={notif.description || ""}
                                isUpdateOnRealtime={true}
                                spreadBehaviour="stretch"
                            />
                            <DateTimePicker
                                initialDate={notif.targetDate ? new Date(notif.targetDate) : undefined}
                                title={localize("announcement-notify-date") || "Gönderim Tarihi"}
                                onChange={({
                                    date
                                }) => {
                                    const newNotify = [
                                        ...(currentLangData.notify || [])
                                    ];
                                    newNotify[index]!.targetDate = date as Date;
                                    updateCurrentLangData("notify", newNotify);
                                }}
                                pickerType="date-time"
                                variant="single"
                            />
                        </View>;
                    })
                }
                <Button
                    title={localize("announcement-notify-add") || "Yeni Bildirim Ekle"}
                    onPress={() => {
                        const newNotify = [
                            ...(currentLangData?.notify || [])
                        ];
                        newNotify.push({
                            targetDate: new Date(),
                            description: "",
                            title: ""
                        });
                        updateCurrentLangData("notify", newNotify);
                    }}
                    icon={({
                        size
                    }) => <PlusIcon
                        color={colors.project.brandWhite}
                        size={size}
                    />}
                    customBorderColor="secondaryButton"
                    style={{
                        marginTop: spaces.spacingMd
                    }}
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    variant="filled"
                />
            </View>
        </View>;
    };

    if(isLoading) {
        return <PageContainer
            isScrollable={true}
        >
            <View
                style={[
                    stylesheet.container,
                    {
                        justifyContent: "center",
                        alignItems: "center"
                    }
                ]}
            >
                <Text>Loading...</Text>
            </View>
        </PageContainer>;
    }

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl,
                    paddingBottom: spaces.spacingMd
                }
            ]}
        >
            {renderHeader()}
            {renderImagesSection()}
            {renderTabs()}
            {renderForm()}
            <View
                style={[
                    stylesheet.bottomBar,
                    {
                        marginTop: spaces.spacingXl,
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View>
                    <Button
                        title={localize("announcement-edit-delete") || "Sil"}
                        onPress={() => setIsDeleteDialogOpen(true)}
                        icon={({
                            color,
                            size
                        }) => <TrashIcon
                            color={colors.content.icon[color]}
                            size={size}
                        />}
                        spreadBehaviour="free"
                        isLoading={isDeleting}
                        type="danger"
                    />
                </View>
                <View
                    style={{
                        flexDirection: "row",
                        gap: spaces.spacingMd
                    }}
                >
                    <Button
                        title={localize("announcement-save-button") || "Kaydet"}
                        customTextColor={colors.project.brandWhite}
                        icon={({
                            color,
                            size
                        }) => <SaveIcon
                            color={colors.content.icon[color]}
                            size={size}
                        />}
                        isLoading={isSubmitting}
                        spreadBehaviour="free"
                        onPress={handleSubmit}
                        customColor="gold"
                    />
                </View>
            </View>
        </View>
        <Dialog
            title={localize("announcement-delete-confirm") || "Silmek istediğinize emin misiniz?"}
            secondaryButtonProps={{
                title: localize("announcement-delete-cancel") || "İptal",
                onPress: () => setIsDeleteDialogOpen(false),
                customTextColor: colors.project.brandWhite,
                customBorderColor: "secondaryButton",
                customColor: "secondaryButton",
                variant: "filled"
            }}
            primaryButtonProps={{
                icon: ({
                    color,
                    size
                }) => <TrashIcon
                    color={colors.content.icon[color as never] || color}
                    size={size}
                />,
                title: localize("announcement-delete-button") || "Sil",
                isLoading: isDeleting,
                onPress: handleDelete,
                type: "danger"
            }}
            onClosed={() => setIsDeleteDialogOpen(false)}
            isVisible={isDeleteDialogOpen}
            variant="yes-no"
        />
        <Dialog
            title={localize("announcement-save-confirm") || "Kaydetmek istediğinize emin misiniz?"}
            primaryButtonProps={{
                title: localize("announcement-save-button") || "Kaydet",
                onPress: handleConfirmSave,
                isLoading: isSubmitting
            }}
            secondaryButtonProps={{
                customTextColor: colors.project.brandWhite,
                onPress: () => setIsDialogVisible(false),
                customBorderColor: "secondaryButton",
                title: localize("cancel") || "İptal",
                customColor: "secondaryButton",
                variant: "filled"
            }}
            onClosed={() => setIsDialogVisible(false)}
            isVisible={isDialogVisible}
            variant="yes-no"
        />
        <DriveSelectorModal
            onClose={() => setIsDriveModalVisible(false)}
            onSelect={handleDriveFileSelect}
            isVisible={isDriveModalVisible}
        />
        <Modal
            onRequestClose={() => setIsLightboxVisible(false)}
            visible={isLightboxVisible}
            animationType="fade"
            transparent={true}
        >
            <View
                style={[
                    stylesheet.modalOverlay,
                    {
                        backgroundColor: colors.system.scrim
                    }
                ]}
            >
                <TouchableOpacity
                    onPress={() => setIsLightboxVisible(false)}
                    style={[
                        stylesheet.lightboxCloseButton,
                        {
                            right: spaces.spacingMd,
                            top: spaces.spacingMd
                        }
                    ]}
                >
                    <XIcon
                        color={colors.project.brandWhite}
                        size={32}
                    />
                </TouchableOpacity>
                <Image
                    style={stylesheet.lightboxImage}
                    source={{
                        uri: coverPreview
                    }}
                    resizeMode="contain"
                />
            </View>
        </Modal>
    </PageContainer>;
};

export default AnnouncementEdit;
